// FE-PINV-064 — <PipelineToolbar>.
//
// Owns the active-filter chip row, the "+ Filter" popover, the
// density quick-toggle (Comfortable / Compact), and the Show closed
// toggle. Lifts state up to the Pipeline-tab shell (W1) so all three
// view bodies (Grid / Kanban / List) react to the same filter set.
//
// The popover applies multi-select edits in a single ``onChange``
// commit on Apply. Apply-on-click is rejected for this iteration to
// batch the underlying queries.

import {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";

import { useFilterCounts } from "./hooks";
import type { FilterCountsBucket, FilterCountsResponse } from "./api";
import {
  EMPTY_PIPELINE_FILTERS,
  activeFacetCount,
  isFiltersEmpty,
  type Density,
  type PipelineCadence,
  type PipelineFacetKey,
  type PipelineFilters,
  type PipelineLivesBand,
  type PipelineOppType,
  type PipelineRenewalWindow,
  type PipelineStage,
} from "./filterState";


// ── Facet metadata ────────────────────────────────────────────────


interface FacetOption {
  value: string;
  label: string;
}


interface FacetSpec {
  key: PipelineFacetKey;
  category: string;
  /** Multi-select facet renders as checkboxes; single-select renders
   * as radio (used by renewal_window) or as a toggle (alerts_only). */
  variant: "multi" | "single" | "boolean";
  options: FacetOption[] | "producer";
}


const CADENCE_OPTIONS: { value: PipelineCadence; label: string }[] = [
  { value: "critical", label: "Critical" },
  { value: "behind", label: "Behind" },
  { value: "on_track", label: "On track" },
  { value: "ahead", label: "Ahead" },
  { value: "not_yet_urgent", label: "Not yet urgent" },
];


const STAGE_OPTIONS: { value: PipelineStage; label: string }[] = [
  { value: "lead", label: "Lead" },
  { value: "meeting_set", label: "Meeting set" },
  { value: "qualified", label: "Qualified" },
  { value: "proposal", label: "Proposal" },
  { value: "negotiation", label: "Negotiation" },
  {
    value: "bor_signed_pending_effective_date",
    label: "BOR signed, pending effective date",
  },
];


const LIVES_OPTIONS: { value: PipelineLivesBand; label: string }[] = [
  { value: "micro", label: "Micro" },
  { value: "small", label: "Small" },
  { value: "mid_market", label: "Mid market" },
  { value: "large", label: "Large" },
  { value: "enterprise", label: "Enterprise" },
];


const RENEWAL_WINDOW_OPTIONS: {
  value: PipelineRenewalWindow; label: string;
}[] = [
  { value: "this_month", label: "This month" },
  { value: "next_30d", label: "Next 30 days" },
  { value: "next_60d", label: "Next 60 days" },
  { value: "next_90d", label: "Next 90 days" },
  { value: "this_quarter", label: "This quarter" },
  { value: "next_quarter", label: "Next quarter" },
];


const OPP_TYPE_OPTIONS: { value: PipelineOppType; label: string }[] = [
  { value: "new_business", label: "New business" },
  { value: "bor_takeover", label: "BOR takeover" },
  { value: "rfp_response", label: "RFP response" },
  { value: "expansion", label: "Expansion" },
];


const FACETS: FacetSpec[] = [
  { key: "cadence", category: "Cadence", variant: "multi", options: CADENCE_OPTIONS },
  { key: "stage", category: "Stage", variant: "multi", options: STAGE_OPTIONS },
  { key: "lives_band", category: "Lives", variant: "multi", options: LIVES_OPTIONS },
  { key: "renewal_window", category: "Renewal window", variant: "single", options: RENEWAL_WINDOW_OPTIONS },
  { key: "opportunity_type", category: "Opp type", variant: "multi", options: OPP_TYPE_OPTIONS },
  { key: "producer", category: "Producer", variant: "multi", options: "producer" },
  { key: "alerts_only", category: "Alerts", variant: "boolean", options: [{ value: "true", label: "Open alerts only" }] },
];


function labelForValue(facet: PipelineFacetKey, value: string): string {
  const spec = FACETS.find((f) => f.key === facet);
  if (!spec || spec.options === "producer") return value;
  return spec.options.find((o) => o.value === value)?.label ?? value;
}


// ── Toolbar ────────────────────────────────────────────────────────


export interface PipelineToolbarProps {
  filters: PipelineFilters;
  onChange: (next: PipelineFilters) => void;
  showClosed: boolean;
  onShowClosedChange: (next: boolean) => void;
  density: Density;
  onDensityChange: (next: Density) => void;
  /** Producer membership ids and display labels for the popover's
   *  Producer facet. Host (W1's shell) typically passes the
   *  team-members API rows through. */
  producerOptions: { id: string; label: string }[];
  /** FE-SCRM-064 (c): density toggle is List-view-only per design.
   *  Grid + Kanban have their own visual rhythm; the toggle is
   *  hidden there. Default true keeps existing callers
   *  (pre-FE-SCRM-064 tests) working. */
  showDensity?: boolean;
  /** FE-SCRM-064 (d): closed_won + closed_lost opportunity count
   *  from BE-SCRM-007's board response (``summary_row``). Surfaces
   *  as a trailing chip badge on the Show closed toggle so the
   *  producer sees how many opps the toggle would reveal. Omit or
   *  pass ``0`` to hide the badge. */
  closedCount?: number;
}


export function PipelineToolbar({
  filters, onChange,
  showClosed, onShowClosedChange,
  density, onDensityChange,
  producerOptions,
  showDensity = true,
  closedCount,
}: PipelineToolbarProps): JSX.Element {
  const [popoverOpen, setPopoverOpen] = useState(false);

  function removeFacet(facet: PipelineFacetKey) {
    const next: PipelineFilters = { ...filters };
    if (facet === "alerts_only") next.alerts_only = false;
    else if (facet === "renewal_window") next.renewal_window = null;
    else (next as Record<PipelineFacetKey, unknown>)[facet] = [];
    onChange(next);
  }

  function clearAll() {
    onChange(EMPTY_PIPELINE_FILTERS);
  }

  const empty = isFiltersEmpty(filters);

  return (
    <div
      data-testid="pipeline-toolbar"
      className="flex flex-wrap items-center gap-2 rounded-md border border-border-subtle bg-recessed px-2 py-1.5"
    >
      {!empty && (
        <ActiveFilterChips
          filters={filters}
          producerOptions={producerOptions}
          onRemove={removeFacet}
        />
      )}
      {!empty && (
        <button
          type="button"
          onClick={clearAll}
          className="text-xs text-text-secondary underline hover:text-text-primary"
        >
          Clear filters
        </button>
      )}

      <button
        type="button"
        onClick={() => setPopoverOpen(true)}
        className="ml-auto inline-flex items-center gap-1 rounded-md border border-border-default bg-raised px-2 py-1 text-xs font-medium text-text-primary hover:border-text-tertiary"
      >
        + Filter
        {activeFacetCount(filters) > 0
          ? ` (${activeFacetCount(filters)})`
          : ""}
      </button>

      {showDensity ? (
        <DensityToggle density={density} onChange={onDensityChange} />
      ) : null}

      <ShowClosedToggle
        showClosed={showClosed}
        onChange={onShowClosedChange}
        count={closedCount}
      />

      {popoverOpen ? (
        <FilterPopover
          filters={filters}
          onApply={(next) => {
            onChange(next);
            setPopoverOpen(false);
          }}
          onCancel={() => setPopoverOpen(false)}
          showClosed={showClosed}
          producerOptions={producerOptions}
        />
      ) : null}
    </div>
  );
}


// ── Chips ──────────────────────────────────────────────────────────


function ActiveFilterChips({
  filters, producerOptions, onRemove,
}: {
  filters: PipelineFilters;
  producerOptions: { id: string; label: string }[];
  onRemove: (facet: PipelineFacetKey) => void;
}) {
  const chips: { key: PipelineFacetKey; category: string; value: string }[] = [];

  if (filters.cadence.length > 0) {
    chips.push({
      key: "cadence",
      category: "Cadence",
      value: filters.cadence.map((v) => labelForValue("cadence", v)).join(", "),
    });
  }
  if (filters.stage.length > 0) {
    chips.push({
      key: "stage",
      category: "Stage",
      value: filters.stage.map((v) => labelForValue("stage", v)).join(", "),
    });
  }
  if (filters.lives_band.length > 0) {
    chips.push({
      key: "lives_band",
      category: "Lives",
      value: filters.lives_band
        .map((v) => labelForValue("lives_band", v))
        .join(", "),
    });
  }
  if (filters.renewal_window !== null) {
    chips.push({
      key: "renewal_window",
      category: "Renewal window",
      value: labelForValue("renewal_window", filters.renewal_window),
    });
  }
  if (filters.opportunity_type.length > 0) {
    chips.push({
      key: "opportunity_type",
      category: "Opp type",
      value: filters.opportunity_type
        .map((v) => labelForValue("opportunity_type", v))
        .join(", "),
    });
  }
  if (filters.producer.length > 0) {
    const labels = filters.producer.map(
      (id) => producerOptions.find((p) => p.id === id)?.label ?? id,
    );
    chips.push({
      key: "producer",
      category: "Producer",
      value: labels.join(", "),
    });
  }
  if (filters.alerts_only) {
    chips.push({
      key: "alerts_only",
      category: "Alerts",
      value: "Open only",
    });
  }

  return (
    <>
      {chips.map((c) => (
        <span
          key={c.key}
          data-testid="active-filter-chip"
          className="inline-flex items-center gap-1 rounded border border-border-subtle bg-raised px-2 py-0.5 text-xs text-text-primary"
        >
          <span className="text-text-tertiary">{c.category}:</span>
          <span>{c.value}</span>
          <button
            type="button"
            aria-label={`Remove ${c.category.toLowerCase()} filter`}
            onClick={() => onRemove(c.key)}
            className="ml-1 text-text-tertiary hover:text-text-primary"
          >
            ×
          </button>
        </span>
      ))}
    </>
  );
}


// ── Density + Show closed ─────────────────────────────────────────


function DensityToggle({
  density, onChange,
}: { density: Density; onChange: (next: Density) => void }) {
  return (
    <div
      role="group"
      aria-label="Row density"
      className="inline-flex overflow-hidden rounded-md border border-border-subtle"
    >
      <button
        type="button"
        aria-pressed={density === "comfortable"}
        onClick={() => onChange("comfortable")}
        className={[
          "px-2 py-1 text-xs",
          density === "comfortable"
            ? "bg-teal-tint text-text-primary"
            : "bg-raised text-text-secondary hover:text-text-primary",
        ].join(" ")}
      >
        Comfortable
      </button>
      <button
        type="button"
        aria-pressed={density === "compact"}
        onClick={() => onChange("compact")}
        className={[
          "px-2 py-1 text-xs",
          density === "compact"
            ? "bg-teal-tint text-text-primary"
            : "bg-raised text-text-secondary hover:text-text-primary",
        ].join(" ")}
      >
        Compact
      </button>
    </div>
  );
}


function ShowClosedToggle({
  showClosed, onChange, count,
}: {
  showClosed: boolean;
  onChange: (next: boolean) => void;
  /** FE-SCRM-064 (d): closed_won + closed_lost opp count from the
   *  board response. Renders as a trailing badge on the chip when
   *  > 0 so producers see what they'd reveal. */
  count?: number;
}) {
  const hasCount = typeof count === "number" && count > 0;
  return (
    <button
      type="button"
      data-testid="pipeline-show-closed-toggle"
      aria-pressed={showClosed}
      onClick={() => onChange(!showClosed)}
      className={[
        "inline-flex items-center gap-1 rounded-full border border-border-subtle px-2 py-1 text-xs",
        showClosed
          ? "bg-teal-tint text-text-primary"
          : "bg-raised text-text-secondary hover:text-text-primary",
      ].join(" ")}
    >
      <span>Show closed</span>
      {hasCount ? (
        <span
          data-testid="pipeline-show-closed-count"
          className={[
            "inline-flex min-w-[1.25rem] items-center justify-center",
            "rounded-full px-1 text-[10px] font-semibold tabular-nums",
            showClosed
              ? "bg-teal/20 text-text-primary"
              : "bg-recessed text-text-secondary",
          ].join(" ")}
        >
          {count}
        </span>
      ) : null}
    </button>
  );
}


// ── + Filter popover ──────────────────────────────────────────────


function FilterPopover({
  filters, onApply, onCancel, showClosed, producerOptions,
}: {
  filters: PipelineFilters;
  onApply: (next: PipelineFilters) => void;
  onCancel: () => void;
  showClosed: boolean;
  producerOptions: { id: string; label: string }[];
}): JSX.Element {
  const [pending, setPending] = useState<PipelineFilters>({ ...filters });
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  // Trapped focus: focus the search input on mount; Esc closes.
  useEffect(() => {
    const input = containerRef.current?.querySelector(
      "input[type=text]",
    ) as HTMLInputElement | null;
    input?.focus();
  }, []);

  // Counts per facet — one query per facet so excluding-self
  // semantics work bucket-by-bucket.
  const cadence = useFilterCounts(filters, {
    excludeFacet: "cadence", showClosed,
  });
  const stage = useFilterCounts(filters, {
    excludeFacet: "stage", showClosed,
  });
  const lives = useFilterCounts(filters, {
    excludeFacet: "lives_band", showClosed,
  });
  const renewal = useFilterCounts(filters, {
    excludeFacet: "renewal_window", showClosed,
  });
  const oppType = useFilterCounts(filters, {
    excludeFacet: "opportunity_type", showClosed,
  });
  const producer = useFilterCounts(filters, {
    excludeFacet: "producer", showClosed,
  });
  const alerts = useFilterCounts(filters, {
    excludeFacet: "alerts_only", showClosed,
  });

  const countsByFacet = useMemo<
    Partial<Record<PipelineFacetKey, FilterCountsBucket | undefined>>
  >(() => ({
    cadence: (cadence.data as FilterCountsResponse | undefined)?.cadence,
    stage: (stage.data as FilterCountsResponse | undefined)?.stage,
    lives_band: (lives.data as FilterCountsResponse | undefined)?.lives_band,
    renewal_window:
      (renewal.data as FilterCountsResponse | undefined)?.renewal_window,
    opportunity_type:
      (oppType.data as FilterCountsResponse | undefined)?.opportunity_type,
    producer: (producer.data as FilterCountsResponse | undefined)?.producer,
    alerts_only:
      (alerts.data as FilterCountsResponse | undefined)?.alerts_only,
  }), [
    cadence.data, stage.data, lives.data, renewal.data,
    oppType.data, producer.data, alerts.data,
  ]);

  function toggleMulti<T extends string>(
    facet: PipelineFacetKey, value: T,
  ) {
    setPending((prev) => {
      const current = ((prev as Record<PipelineFacetKey, unknown>)[facet]
        ?? []) as T[];
      const has = current.includes(value);
      const next = has
        ? current.filter((v) => v !== value)
        : [...current, value];
      return {
        ...prev,
        [facet]: next,
      } as PipelineFilters;
    });
  }

  function setSingle(facet: PipelineFacetKey, value: string | null) {
    setPending((prev) => ({ ...prev, [facet]: value }));
  }

  function setBoolean(facet: PipelineFacetKey, value: boolean) {
    setPending((prev) => ({ ...prev, [facet]: value }));
  }

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
    },
    [onCancel],
  );

  const lowerSearch = search.trim().toLowerCase();
  const facetsWithProducer: FacetSpec[] = FACETS.map((f) =>
    f.options === "producer"
      ? { ...f, options: producerOptions.map((p) => ({ value: p.id, label: p.label })) }
      : f,
  );

  const filtered: FacetSpec[] = lowerSearch === ""
    ? facetsWithProducer
    : facetsWithProducer.flatMap((f) => {
        const matchedOptions = (f.options as FacetOption[]).filter(
          (o) =>
            o.label.toLowerCase().includes(lowerSearch)
            || f.category.toLowerCase().includes(lowerSearch),
        );
        return matchedOptions.length === 0
          ? []
          : [{ ...f, options: matchedOptions }];
      });

  return (
    <div
      ref={containerRef}
      data-testid="filter-popover"
      role="dialog"
      aria-label="+ Filter"
      tabIndex={-1}
      onKeyDown={onKeyDown}
      className="absolute z-30 mt-2 flex max-h-[60vh] w-[24rem] flex-col rounded-md border border-border-default bg-raised shadow-overlay"
    >
      <div className="border-b border-border-subtle p-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search filters…"
          className="w-full rounded border border-border-default bg-raised px-2 py-1 text-sm text-text-primary placeholder:text-text-tertiary focus-visible:border-teal focus-visible:shadow-focus"
        />
      </div>

      <div className="flex-1 overflow-auto p-2">
        {filtered.map((f) => (
          <FacetSection
            key={f.key}
            spec={f as FacetSpec & { options: FacetOption[] }}
            pending={pending}
            counts={countsByFacet[f.key]}
            onToggle={toggleMulti}
            onSetSingle={setSingle}
            onSetBoolean={setBoolean}
          />
        ))}
        {filtered.length === 0 ? (
          <p className="px-2 py-3 text-xs text-text-tertiary">
            No filters match "{search}".
          </p>
        ) : null}
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-border-subtle p-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-border-default bg-raised px-2 py-1 text-xs text-text-secondary hover:text-text-primary"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onApply(pending)}
          className="rounded-md bg-teal px-2 py-1 text-xs font-medium text-white hover:bg-teal-pressed"
        >
          Apply
        </button>
      </div>
    </div>
  );
}


function FacetSection({
  spec, pending, counts, onToggle, onSetSingle, onSetBoolean,
}: {
  spec: FacetSpec & { options: FacetOption[] };
  pending: PipelineFilters;
  counts: FilterCountsBucket | undefined;
  onToggle: <T extends string>(facet: PipelineFacetKey, value: T) => void;
  onSetSingle: (facet: PipelineFacetKey, value: string | null) => void;
  onSetBoolean: (facet: PipelineFacetKey, value: boolean) => void;
}): JSX.Element {
  return (
    <section className="mb-3">
      <h3 className="mb-1 px-2 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
        {spec.category}
      </h3>
      <ul className="flex flex-col">
        {spec.options.map((opt) => {
          const count = counts?.[opt.value];
          const labelText = count !== undefined
            ? `${opt.label} (${count})`
            : opt.label;
          if (spec.variant === "multi") {
            const list = ((pending as Record<PipelineFacetKey, unknown>)[
              spec.key
            ] ?? []) as string[];
            const checked = list.includes(opt.value);
            return (
              <li key={opt.value}>
                <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-recessed">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle(spec.key, opt.value)}
                    aria-label={labelText}
                  />
                  <span>{labelText}</span>
                </label>
              </li>
            );
          }
          if (spec.variant === "single") {
            const cur = pending[spec.key] as string | null;
            const checked = cur === opt.value;
            return (
              <li key={opt.value}>
                <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-recessed">
                  <input
                    type="radio"
                    name={`facet-${spec.key}`}
                    checked={checked}
                    onChange={() =>
                      onSetSingle(spec.key, checked ? null : opt.value)
                    }
                    aria-label={labelText}
                  />
                  <span>{labelText}</span>
                </label>
              </li>
            );
          }
          // boolean — single checkbox
          const checked = pending[spec.key] === true;
          return (
            <li key={opt.value}>
              <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-recessed">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onSetBoolean(spec.key, !checked)}
                  aria-label={labelText}
                />
                <span>{labelText}</span>
              </label>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
